import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Grade } from '../types/domain';
import {
  resolveGradeIdInList,
  saveUserWorkspaceUiPreferences,
} from '../services/userWorkspaceUiPreferencesService';

export interface ProgramSidebarContextValue {
  /** 고정·프로젝트·편제별 프로그램 메뉴 기준 클래스(학년 ID). null = 미선택(클래스 없음) */
  programFocusGradeId: string | null;
  setProgramFocusGradeId: (id: string | null) => void;
  /** 사이드바에서 클래스 선택 — 서버 저장 + 상단 네비 클래스 동기화 */
  selectProgramFocusGrade: (gradeId: string) => void;
  /**
   * @deprecated 통합 클래스 보기 제거. 호출 시 첫 클래스를 선택한다(레거시 호환).
   */
  selectIntegratedClassView: () => void;
  grades: Grade[];
  workspaceId: string;
  /** 사이드바·대시보드 등과 동기되는 학년도(학사일정 섹션·DB 기준으로 페이지에서 갱신) */
  programSidebarAcademicYear: number | null;
  setProgramSidebarAcademicYear: (year: number | null) => void;
}

const ProgramSidebarContext = createContext<ProgramSidebarContextValue | null>(null);

export const ProgramSidebarProvider: React.FC<{
  children: React.ReactNode;
  grades: Grade[];
  workspaceId: string;
  /** null이면 첫 클래스로 초기화 */
  initialSidebarFocusGradeId?: string | null;
  /** 상단 네비 선택값 */
  navGradeId?: string | null;
  onProgramFocusGradeChange?: (gradeId: string) => void;
  onSelectIntegratedClassView?: () => void;
}> = ({
  children,
  grades,
  workspaceId,
  initialSidebarFocusGradeId = null,
  navGradeId = null,
  onProgramFocusGradeChange,
  onSelectIntegratedClassView,
}) => {
  const [programFocusGradeId, setProgramFocusGradeIdState] = useState<string | null>(null);
  const [programSidebarAcademicYear, setProgramSidebarAcademicYear] = useState<number | null>(null);
  const initializedForWorkspaceRef = useRef<string | null>(null);
  const onProgramFocusGradeChangeRef = useRef(onProgramFocusGradeChange);
  onProgramFocusGradeChangeRef.current = onProgramFocusGradeChange;
  const onSelectIntegratedClassViewRef = useRef(onSelectIntegratedClassView);
  onSelectIntegratedClassViewRef.current = onSelectIntegratedClassView;
  const prevNavGradeIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!workspaceId) return;
    if (initializedForWorkspaceRef.current === workspaceId) return;
    initializedForWorkspaceRef.current = workspaceId;

    if (grades.length === 0) {
      setProgramFocusGradeIdState(null);
      return;
    }

    setProgramFocusGradeIdState(
      resolveGradeIdInList(initialSidebarFocusGradeId, grades) ?? grades[0]?.id ?? null
    );
  }, [workspaceId, grades, initialSidebarFocusGradeId]);

  useEffect(() => {
    if (grades.length === 0) {
      setProgramFocusGradeIdState(null);
      return;
    }
    setProgramFocusGradeIdState((prev) => {
      if (prev && grades.some((g) => g.id === prev)) return prev;
      return resolveGradeIdInList(initialSidebarFocusGradeId, grades) ?? grades[0]?.id ?? null;
    });
  }, [grades, initialSidebarFocusGradeId]);

  /** 네비 클래스 변경 시 사이드바 포커스 동기화 */
  useEffect(() => {
    const normalized = (navGradeId ?? '').trim();
    const prev = prevNavGradeIdRef.current;
    prevNavGradeIdRef.current = normalized;
    if (prev === undefined) return;
    if (!normalized) {
      if (grades.length === 0) {
        setProgramFocusGradeIdState(null);
        return;
      }
      const fallback = grades[0]?.id ?? null;
      if (fallback) setProgramFocusGradeIdState(fallback);
      return;
    }
    if (!grades.some((g) => g.id === normalized)) return;
    setProgramFocusGradeIdState(normalized);
  }, [navGradeId, workspaceId, grades]);

  const setProgramFocusGradeId = useCallback((id: string | null) => {
    setProgramFocusGradeIdState(id);
  }, []);

  const selectProgramFocusGrade = useCallback(
    (gradeId: string) => {
      if (!grades.some((g) => g.id === gradeId)) return;
      setProgramFocusGradeIdState(gradeId);
      onProgramFocusGradeChangeRef.current?.(gradeId);
      void saveUserWorkspaceUiPreferences(workspaceId, {
        sidebarFocusGradeId: gradeId,
        navIntegratedView: false,
        navGradeId: gradeId,
      }).catch((err) => {
        console.error('사이드바 클래스 선택 저장 실패:', err);
      });
    },
    [grades, workspaceId]
  );

  const selectIntegratedClassView = useCallback(() => {
    const fallback = grades[0]?.id ?? null;
    if (fallback) {
      setProgramFocusGradeIdState(fallback);
      onProgramFocusGradeChangeRef.current?.(fallback);
      void saveUserWorkspaceUiPreferences(workspaceId, {
        sidebarFocusGradeId: fallback,
        navIntegratedView: false,
        navGradeId: fallback,
      }).catch((err) => {
        console.error('사이드바 클래스 선택 저장 실패:', err);
      });
      return;
    }
    setProgramFocusGradeIdState(null);
    onSelectIntegratedClassViewRef.current?.();
  }, [grades, workspaceId]);

  const value = useMemo(
    () => ({
      programFocusGradeId,
      setProgramFocusGradeId,
      selectProgramFocusGrade,
      selectIntegratedClassView,
      grades,
      workspaceId,
      programSidebarAcademicYear,
      setProgramSidebarAcademicYear,
    }),
    [
      programFocusGradeId,
      setProgramFocusGradeId,
      selectProgramFocusGrade,
      selectIntegratedClassView,
      grades,
      workspaceId,
      programSidebarAcademicYear,
    ]
  );

  return <ProgramSidebarContext.Provider value={value}>{children}</ProgramSidebarContext.Provider>;
};

export function useProgramSidebarOptional(): ProgramSidebarContextValue | null {
  return useContext(ProgramSidebarContext);
}

export function useProgramSidebar(): ProgramSidebarContextValue {
  const ctx = useContext(ProgramSidebarContext);
  if (!ctx) {
    throw new Error('useProgramSidebar must be used within ProgramSidebarProvider');
  }
  return ctx;
}
